/**
 * Timer React Query Hooks
 *
 * Provides hooks for managing the active timer and pending time summaries.
 * Only one timer can be active at a time globally.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';

// =============================================================================
// TYPES (re-exported, now owned here)
// =============================================================================

export interface TimerSession {
  id: string;
  taskId: string;
  taskTitle?: string;
  projectId: string;
  projectTitle?: string;
  description?: string;
  startedAt: string;
}

export interface PendingTimerSummary {
  id: string;
  taskId: string;
  taskTitle?: string;
  projectId: string;
  projectTitle?: string;
  description?: string;
  startedAt: string;
  stoppedAt: string;
  durationMinutes: number;
}

// =============================================================================
// GRAPHQL
// =============================================================================

const ACTIVE_TIMER_QUERY = `
  query ActiveTimer($userId: String) {
    activeTimer(userId: $userId) {
      id
      taskId
      taskTitle
      projectId
      projectTitle
      description
      startedAt
    }
  }
`;

const PENDING_TIMER_SUMMARIES_QUERY = `
  query PendingTimerSummaries($userId: String) {
    pendingTimerSummaries(userId: $userId) {
      id
      taskId
      taskTitle
      projectId
      projectTitle
      description
      startedAt
      stoppedAt
      durationMinutes
    }
  }
`;

const SAVE_ACTIVE_TIMER_MUTATION = `
  mutation SaveActiveTimer($input: ActiveTimerInput!, $userId: String) {
    saveActiveTimer(input: $input, userId: $userId) {
      id
      taskId
      taskTitle
      projectId
      projectTitle
      description
      startedAt
    }
  }
`;

const DELETE_ACTIVE_TIMER_MUTATION = `
  mutation DeleteActiveTimer($userId: String) {
    deleteActiveTimer(userId: $userId)
  }
`;

const CREATE_PENDING_TIMER_SUMMARY_MUTATION = `
  mutation CreatePendingTimerSummary($input: PendingTimerSummaryInput!, $userId: String) {
    createPendingTimerSummary(input: $input, userId: $userId) {
      id
      taskId
      taskTitle
      projectId
      projectTitle
      description
      startedAt
      stoppedAt
      durationMinutes
    }
  }
`;

const DELETE_PENDING_TIMER_SUMMARY_MUTATION = `
  mutation DeletePendingTimerSummary($id: ID!) {
    deletePendingTimerSummary(id: $id)
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

/**
 * Map GraphQL timer to local type
 */
function mapGraphQLTimer(data: any): TimerSession | null {
  if (!data) return null;
  return {
    id: data.id,
    taskId: data.taskId,
    taskTitle: data.taskTitle || undefined,
    projectId: data.projectId,
    projectTitle: data.projectTitle || undefined,
    description: data.description || undefined,
    startedAt: data.startedAt,
  };
}

/**
 * Map GraphQL pending summary to local type
 */
function mapGraphQLSummary(data: any): PendingTimerSummary {
  return {
    id: data.id,
    taskId: data.taskId,
    taskTitle: data.taskTitle || undefined,
    projectId: data.projectId,
    projectTitle: data.projectTitle || undefined,
    description: data.description || undefined,
    startedAt: data.startedAt,
    stoppedAt: data.stoppedAt,
    durationMinutes: data.durationMinutes,
  };
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch the active timer (if any).
 */
export function useActiveTimer() {
  return useQuery({
    queryKey: queryKeys.timer.active(),
    queryFn: async () => {
      const response = await graphqlFetch<{ activeTimer: any }>(ACTIVE_TIMER_QUERY, {
        userId: null,
      });
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
      }
      return mapGraphQLTimer(response.data?.activeTimer);
    },
    staleTime: 30_000, // 30 seconds - near real-time
  });
}

/**
 * Fetch pending timer summaries.
 */
export function usePendingTimerSummaries() {
  return useQuery({
    queryKey: [...queryKeys.timer.all(), 'pending'],
    queryFn: async () => {
      const response = await graphqlFetch<{ pendingTimerSummaries: any[] }>(
        PENDING_TIMER_SUMMARIES_QUERY,
        { userId: null }
      );
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.pendingTimerSummaries || []).map(mapGraphQLSummary);
    },
    staleTime: 30_000, // 30 seconds - near real-time
  });
}

/**
 * Start a new timer (saves to server).
 */
export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (timer: {
      taskId: string;
      taskTitle?: string;
      projectId: string;
      projectTitle?: string;
      description?: string;
    }) => {
      const input = {
        task_id: timer.taskId,
        task_title: timer.taskTitle || null,
        project_id: timer.projectId,
        project_title: timer.projectTitle || null,
        description: timer.description || null,
        started_at: new Date().toISOString(),
      };
      const response = await graphqlFetch<{ saveActiveTimer: any }>(
        SAVE_ACTIVE_TIMER_MUTATION,
        { input, userId: null }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.saveActiveTimer) {
        return mapGraphQLTimer(response.data.saveActiveTimer);
      }
      throw new Error('Failed to start timer');
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.timer.active(), data);
    },
  });
}

/**
 * Stop the active timer (creates a pending summary).
 */
export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (activeTimer: TimerSession) => {
      const stoppedAt = new Date().toISOString();
      const startMs = new Date(activeTimer.startedAt).getTime();
      const stopMs = new Date(stoppedAt).getTime();
      const durationMinutes = Math.round((stopMs - startMs) / 60000);

      // Create pending summary
      const summaryInput = {
        task_id: activeTimer.taskId,
        task_title: activeTimer.taskTitle || null,
        project_id: activeTimer.projectId,
        project_title: activeTimer.projectTitle || null,
        description: activeTimer.description || null,
        started_at: activeTimer.startedAt,
        stopped_at: stoppedAt,
        duration_minutes: durationMinutes,
      };
      const summaryResponse = await graphqlFetch<{ createPendingTimerSummary: any }>(
        CREATE_PENDING_TIMER_SUMMARY_MUTATION,
        { input: summaryInput, userId: null }
      );

      // Delete active timer
      const deleteResponse = await graphqlFetch<{ deleteActiveTimer: boolean }>(
        DELETE_ACTIVE_TIMER_MUTATION,
        { userId: null }
      );

      if (summaryResponse.errors || deleteResponse.errors) {
        throw new Error('Failed to stop timer');
      }

      return mapGraphQLSummary(summaryResponse.data?.createPendingTimerSummary);
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.timer.active(), null);
      qc.invalidateQueries({ queryKey: [...queryKeys.timer.all(), 'pending'] });
    },
  });
}

/**
 * Update the active timer's description.
 */
export function useUpdateTimerDescription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ timer, description }: { timer: TimerSession; description: string }) => {
      const input = {
        task_id: timer.taskId,
        task_title: timer.taskTitle || null,
        project_id: timer.projectId,
        project_title: timer.projectTitle || null,
        description,
        started_at: timer.startedAt,
      };
      const response = await graphqlFetch<{ saveActiveTimer: any }>(
        SAVE_ACTIVE_TIMER_MUTATION,
        { input, userId: null }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.saveActiveTimer) {
        return mapGraphQLTimer(response.data.saveActiveTimer);
      }
      throw new Error('Failed to update timer description');
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.timer.active(), data);
    },
  });
}

/**
 * Discard a pending timer summary.
 */
export function useDiscardPendingSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deletePendingTimerSummary: boolean }>(
        DELETE_PENDING_TIMER_SUMMARY_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deletePendingTimerSummary) {
        throw new Error('Failed to discard pending summary');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.timer.all(), 'pending'] });
    },
  });
}

/**
 * Confirm a pending timer summary (converts to time entry).
 * This mutation should create the actual time entry and delete the pending summary.
 */
export function useConfirmPendingSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      description,
      billable,
    }: {
      id: string;
      description?: string;
      billable?: boolean;
    }) => {
      // This would typically call a mutation that:
      // 1. Creates a TimeEntry from the pending summary
      // 2. Deletes the pending summary
      // For now, just delete the pending summary
      const response = await graphqlFetch<{ deletePendingTimerSummary: boolean }>(
        DELETE_PENDING_TIMER_SUMMARY_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.timer.all(), 'pending'] });
      qc.invalidateQueries({ queryKey: queryKeys.timer.entries() });
    },
  });
}

// =============================================================================
// HELPER HOOKS
// =============================================================================

/**
 * React hook that returns the elapsed time in seconds for a running timer.
 * Updates every second.
 */
export function useElapsedTime(startedAt: string | undefined): number {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) {
      setElapsed(0);
      return;
    }

    const calculateElapsed = () => {
      const now = Date.now();
      const start = new Date(startedAt).getTime();
      return Math.floor((now - start) / 1000);
    };

    setElapsed(calculateElapsed());

    const interval = setInterval(() => {
      setElapsed(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [startedAt]);

  return elapsed;
}

/**
 * Format duration in minutes to human-readable string.
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
